import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { ChevronLeft, ChevronRight, Star, ShoppingCart, Eye } from 'lucide-react';
import { useCart } from './Cart';
import { useToast } from './Toast';
import { useNavigate } from 'react-router-dom';
import categoryService from '../services/categoryService';

const SimilarProductsContainer = styled.div`
  margin: 3rem 0;
  padding: 2rem 0;
  background: rgba(0, 0, 0, 0.2);
  border-radius: 1rem;
  border: 1px solid rgba(255, 255, 255, 0.1);
`;

const SectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
  padding: 0 1rem;

  h2 {
    color: white;
    font-size: 1.8rem;
    font-weight: 600;
    margin: 0;
  }

  .view-all {
    color: #3b82f6;
    text-decoration: none;
    font-weight: 500;
    transition: color 0.2s ease;

    &:hover {
      color: #60a5fa;
    }
  }
`;

const SliderContainer = styled.div`
  position: relative;
  overflow: hidden;
  border-radius: 0.75rem;
`;

const SliderWrapper = styled.div`
  display: flex;
  transition: transform 0.3s ease;
  gap: 1.5rem;
  padding: 0 1rem;
`;

const ProductCard = styled.div`
  flex: 0 0 280px;
  background: #ffffff;
  border: 1px solid rgba(0, 0, 0, 0.1);
  border-radius: 0.75rem;
  overflow: hidden;
  transition: all 0.3s ease;
  cursor: pointer;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);

  &:hover {
    transform: translateY(-4px);
    background: #ffffff;
    border-color: rgba(59, 130, 246, 0.4);
    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.2);
  }

  .product-image {
    width: 100%;
    height: 200px;
    overflow: hidden;
    position: relative;

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
      transition: transform 0.3s ease;
    }

    &:hover img {
      transform: scale(1.05);
    }

    .quick-view {
      position: absolute;
      top: 0.5rem;
      right: 0.5rem;
      background: rgba(0, 0, 0, 0.7);
      color: white;
      border: none;
      border-radius: 50%;
      width: 2.5rem;
      height: 2.5rem;
      display: flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;
      opacity: 0;
      transition: all 0.2s ease;

      &:hover {
        background: rgba(59, 130, 246, 0.8);
      }
    }

    &:hover .quick-view {
      opacity: 1;
    }
  }

  .product-info {
    padding: 1rem;

    .product-name {
      color: #1a1a1a;
      font-size: 1rem;
      font-weight: 600;
      margin-bottom: 0.5rem;
      line-height: 1.4;
    }

    .product-rating {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      margin-bottom: 0.75rem;

      .stars {
        display: flex;
        gap: 0.1rem;

        .star {
          color: #fbbf24;
          font-size: 0.9rem;
        }
      }

      .rating-text {
        color: #6b7280;
        font-size: 0.8rem;
        font-weight: 500;
      }
    }

    .product-price {
      color: #1a1a1a;
      font-size: 1.25rem;
      font-weight: 700;
      margin-bottom: 1rem;
    }

    .product-actions {
      display: flex;
      gap: 0.5rem;

      .add-to-cart {
        flex: 1;
        background: linear-gradient(135deg, #3b82f6, #9333ea);
        color: white;
        border: none;
        border-radius: 0.5rem;
        padding: 0.5rem 1rem;
        font-size: 0.8rem;
        font-weight: 600;
        cursor: pointer;
        transition: all 0.2s ease;
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 0.25rem;

        &:hover {
          transform: translateY(-1px);
          box-shadow: 0 5px 15px rgba(59, 130, 246, 0.3);
        }

        &:active {
          transform: translateY(0);
        }
      }
    }
  }
`;

const NavigationButton = styled.button`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  color: white;
  width: 3rem;
  height: 3rem;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  z-index: 10;

  &:hover {
    background: rgba(255, 255, 255, 0.2);
    border-color: rgba(59, 130, 246, 0.5);
  }

  &:disabled {
    opacity: 0.3;
    cursor: not-allowed;

    &:hover {
      background: rgba(255, 255, 255, 0.1);
      border-color: rgba(255, 255, 255, 0.2);
    }
  }

  &.prev {
    left: 1rem;
  }

  &.next {
    right: 1rem;
  }
`;

const LoadingState = styled.div`
  display: flex;
  gap: 1.5rem;
  padding: 0 1rem;

  .skeleton-card {
    flex: 0 0 280px;
    background: rgba(255, 255, 255, 0.05);
    border-radius: 0.75rem;
    overflow: hidden;

    .skeleton-image {
      width: 100%;
      height: 200px;
      background: linear-gradient(90deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0.2) 50%, rgba(255, 255, 255, 0.1) 100%);
      background-size: 200% 100%;
      animation: shimmer 1.5s infinite;
    }

    .skeleton-content {
      padding: 1rem;
      gap: 0.5rem;

      .skeleton-line {
        height: 1rem;
        background: linear-gradient(90deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0.2) 50%, rgba(255, 255, 255, 0.1) 100%);
        background-size: 200% 100%;
        animation: shimmer 1.5s infinite;
        border-radius: 0.25rem;

        &:nth-child(1) { width: 80%; }
        &:nth-child(2) { width: 60%; }
        &:nth-child(3) { width: 40%; }
      }
    }
  }

  @keyframes shimmer {
    0% { background-position: -200% 0; }
    100% { background-position: 200% 0; }
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 3rem;
  color: rgba(255, 255, 255, 0.6);

  p {
    font-size: 1.1rem;
    margin: 0;
  }
`;

const SimilarProducts = ({ currentProduct, currentCategory }) => {
  const [similarProducts, setSimilarProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentIndex, setCurrentIndex] = useState(0);
  const { addToCart } = useCart();
  const { addToast } = useToast();
  const navigate = useNavigate();

  // Charger les produits similaires
  useEffect(() => {
    const loadSimilarProducts = async () => {
      try {
        setLoading(true);
        const products = await categoryService.getSimilarProducts(
          currentProduct?.id, 
          currentCategory, 
          8
        );
        setSimilarProducts(products);
      } catch (error) {
        console.error('Erreur lors du chargement des produits similaires:', error);
      } finally {
        setLoading(false);
      }
    };

    if (currentCategory && currentProduct) {
      loadSimilarProducts();
    }
  }, [currentCategory, currentProduct]);

  const handleAddToCart = (product, e) => {
    e.stopPropagation();
    
    const configuration = {
      storage: product.storage_options?.[0]?.name || '128GB',
      color: product.colors?.[0]?.name || 'Noir'
    };
    
    const offer = null; // Pas d'offre flash pour les produits similaires
    
    addToCart(product, configuration, offer);
    addToast(`${product.name} ajouté au panier !`, { type: 'success' });
  };

  const handleQuickView = (product, e) => {
    e.stopPropagation();
    // Naviguer vers la page du produit
    navigate(`/product/${product.slug || product.id}`);
  };

  const handleProductClick = (product) => {
    navigate(`/product/${product.slug || product.id}`);
  };

  const slideLeft = () => {
    setCurrentIndex(prev => Math.max(0, prev - 1));
  };

  const slideRight = () => {
    const maxIndex = Math.max(0, similarProducts.length - 4);
    setCurrentIndex(prev => Math.min(maxIndex, prev + 1));
  };

  const canSlideLeft = currentIndex > 0;
  const canSlideRight = currentIndex < Math.max(0, similarProducts.length - 4);

  if (loading) {
    return (
      <SimilarProductsContainer>
        <SectionHeader>
          <h2>Produits similaires</h2>
        </SectionHeader>
        <LoadingState>
          {[1, 2, 3, 4].map(i => (
            <div key={i} className="skeleton-card">
              <div className="skeleton-image" />
              <div className="skeleton-content">
                <div className="skeleton-line" />
                <div className="skeleton-line" />
                <div className="skeleton-line" />
              </div>
            </div>
          ))}
        </LoadingState>
      </SimilarProductsContainer>
    );
  }

  if (similarProducts.length === 0) {
    return (
      <SimilarProductsContainer>
        <SectionHeader>
          <h2>Produits similaires</h2>
        </SectionHeader>
        <EmptyState>
          <p>Aucun produit similaire trouvé dans cette catégorie</p>
        </EmptyState>
      </SimilarProductsContainer>
    );
  }

  return (
    <SimilarProductsContainer>
      <SectionHeader>
        <h2>Produits similaires</h2>
        <a href={`/category/${currentCategory?.toLowerCase()}`} className="view-all">
          Voir tout →
        </a>
      </SectionHeader>

      <SliderContainer>
        <NavigationButton 
          className="prev" 
          onClick={slideLeft}
          disabled={!canSlideLeft}
        >
          <ChevronLeft size={20} />
        </NavigationButton>

        <NavigationButton 
          className="next" 
          onClick={slideRight}
          disabled={!canSlideRight}
        >
          <ChevronRight size={20} />
        </NavigationButton>

        <SliderWrapper 
          style={{ transform: `translateX(-${currentIndex * 310}px)` }}
        >
          {similarProducts.map((product) => (
            <ProductCard 
              key={product.id} 
              onClick={() => handleProductClick(product)}
            >
              <div className="product-image">
                <img 
                  src={product.image 
                    ? `https://inza-backend.sajholding.org${product.image}` 
                    : '/api/placeholder/280/200'
                  } 
                  alt={product.name}
                />
                <button 
                  className="quick-view"
                  onClick={(e) => handleQuickView(product, e)}
                  title="Voir les détails"
                >
                  <Eye size={16} />
                </button>
              </div>

              <div className="product-info">
                <h3 className="product-name">{product.name}</h3>
                
                <div className="product-rating">
                  <div className="stars">
                    {[1, 2, 3, 4, 5].map(star => (
                      <Star 
                        key={star} 
                        className="star" 
                        fill={star <= Math.round(product.rating || product.average_rating || 0) ? 'currentColor' : 'none'}
                        size={12}
                      />
                    ))}
                  </div>
                  <span className="rating-text">
                    {(product.rating || product.average_rating || 0).toFixed(1)} ({product.review_count || product.reviews_count || 0})
                  </span>
                </div>

                <div className="product-price">
                  {parseFloat(product.price || 0).toLocaleString()} FCFA
                </div>

                <div className="product-actions">
                  <button 
                    className="add-to-cart"
                    onClick={(e) => handleAddToCart(product, e)}
                  >
                    <ShoppingCart size={14} />
                    Ajouter
                  </button>
                </div>
              </div>
            </ProductCard>
          ))}
        </SliderWrapper>
      </SliderContainer>
    </SimilarProductsContainer>
  );
};

export default SimilarProducts;
